import { Container, PortalLayout, RenewalSummaryCard, Stack, Typography } from "../../design-system";
import { formatLongDate } from "../../content/format-long-date";
import { formatPolicyLine } from "../../content/policy-line";
import { resolveOwnerName } from "../../content/owner-name";
import type { PolicyInScope } from "../../state/renewal-summary-port";
import { receivedContent as content } from "./content";

/**
 * Renewal received route (Screen 7, AC-22) — the end of the flow for
 * whichever of the four H2 states applies: `owner-submitted`,
 * `owner-saved`, `colleague-submitted`, `colleague-saved`. Presentational,
 * no router import, no I/O: every value is supplied by whatever wires
 * this route (App.tsx), read through the renewal data port.
 *
 * Owner states render the shared `RenewalSummaryCard` head with the
 * calendar/shield meta rows (H2's own markup for both); colleague states
 * render the card with no meta rows at all — neither shows a stepper or
 * any action button (design handoff: "No card [border], no action
 * buttons" — this component accepts no onClick/onSubmit callback prop,
 * there being nothing left for a caller to wire up once the flow has
 * ended).
 *
 * Page ground is the portal's tinted surface, as H2's 07-PostSubmission screen draws it
 * (`bodyGround: C.neutral.surface`); ADR-20261102-h2fd supersedes the earlier white ground
 * (OBJ-27/FIND-43), which followed a pre-H2 handoff note.
 */
export type ReceivedRouteState = "owner-submitted" | "owner-saved" | "colleague-submitted" | "colleague-saved";

export interface ReceivedRouteProps {
  businessName: string;
  /** ISO 8601 (YYYY-MM-DD). Read through the renewal data port, never
   * hardcoded (AC-10's "no value is invented" discipline, carried over
   * to this screen by AC-22/AC-30). */
  renewalDate: string;
  policiesInScope: readonly PolicyInScope[];
  /** The renewal owner's name — shown only in the two colleague states
   * (AC-22). Ignored for the two owner states. `null` for an instance
   * exposure-prepared before this feature shipped (ADR-20261016-mew4) —
   * this component substitutes the Content Design Agent's own fallback
   * copy (`content/owner-name.ts`) in that case, never a blank. */
  ownerName: string | null;
  state: ReceivedRouteState;
}

function ownerMetaRows(renewalDate: string, policiesInScope: readonly PolicyInScope[]) {
  return [
    {
      icon: "calendar" as const,
      content: (
        <>
          Renews{" "}
          <Typography variant="body" as="time" dateTime={renewalDate}>
            {formatLongDate(renewalDate)}
          </Typography>
        </>
      ),
    },
    { icon: "shield" as const, content: formatPolicyLine(policiesInScope) },
  ];
}

export function ReceivedRoute({
  businessName,
  renewalDate,
  policiesInScope,
  ownerName,
  state,
}: Readonly<ReceivedRouteProps>) {
  const deadline = formatLongDate(renewalDate);

  if (state === "owner-submitted") {
    return (
      <PortalLayout businessName={businessName}>
        <Container layout="summary">
          <RenewalSummaryCard businessName={businessName} metaRows={ownerMetaRows(renewalDate, policiesInScope)}>
            <Typography variant="bodyLarge">
              {content.ownerSubmitted.confirmationPrefix}
              {businessName}
              {content.ownerSubmitted.confirmationSuffix}
            </Typography>
            <Stack gap="xs">
              <Typography variant="heading3" as="h2">
                {content.ownerSubmitted.whatHappensNextHeading}
              </Typography>
              <Stack gap="field">
                <Typography variant="bodyLarge">
                  {content.ownerSubmitted.whatHappensNextPrefix}
                  <strong>{deadline}</strong>
                  {content.ownerSubmitted.whatHappensNextSuffix}
                </Typography>
                <Typography variant="bodyLarge">{content.ownerSubmitted.followUp}</Typography>
                <Typography variant="bodyLarge">{content.ownerSubmitted.thanks}</Typography>
              </Stack>
            </Stack>
          </RenewalSummaryCard>
        </Container>
      </PortalLayout>
    );
  }

  if (state === "owner-saved") {
    return (
      <PortalLayout businessName={businessName}>
        <Container layout="summary">
          <RenewalSummaryCard businessName={businessName} metaRows={ownerMetaRows(renewalDate, policiesInScope)}>
            <Typography variant="bodyLarge">{content.ownerSaved.statusMessage}</Typography>
            <Stack gap="xs">
              <Typography variant="heading3" as="h2">
                {content.ownerSaved.whatHappensNextHeading}
              </Typography>
              <Stack gap="field">
                <Typography variant="bodyLarge">{content.ownerSaved.whatHappensNext}</Typography>
                <Typography variant="bodyLarge">{content.ownerSaved.followUp}</Typography>
              </Stack>
            </Stack>
          </RenewalSummaryCard>
        </Container>
      </PortalLayout>
    );
  }

  if (state === "colleague-submitted") {
    return (
      <PortalLayout businessName={businessName}>
        <Container layout="summary">
          <RenewalSummaryCard businessName={businessName}>
            <Typography variant="bodyLarge">
              {content.colleagueSubmitted.confirmationPrefix}
              {businessName}
              {content.colleagueSubmitted.confirmationSuffix}
            </Typography>
            <Typography variant="bodyLarge">
              {content.colleagueSubmitted.sentToPrefix}
              {resolveOwnerName(ownerName)}
              {content.colleagueSubmitted.sentToSuffix}
            </Typography>
            <Stack gap="xs">
              <Typography variant="heading3" as="h2">
                {content.colleagueSubmitted.whatHappensNextHeading}
              </Typography>
              <Typography variant="bodyLarge">{content.colleagueSubmitted.whatHappensNext(ownerName)}</Typography>
            </Stack>
          </RenewalSummaryCard>
        </Container>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout businessName={businessName}>
      <Container layout="summary">
        <RenewalSummaryCard businessName={businessName}>
          <Typography variant="bodyLarge">
            {content.colleagueSaved.statusMessagePrefix}
            {resolveOwnerName(ownerName)}
            {content.colleagueSaved.statusMessageSuffix}
          </Typography>
          <Stack gap="xs">
            <Typography variant="heading3" as="h2">
              {content.colleagueSaved.whatHappensNextHeading}
            </Typography>
            <Stack gap="field">
              <Typography variant="bodyLarge">{content.colleagueSaved.whatHappensNext}</Typography>
              <Typography variant="bodyLarge">{content.colleagueSaved.followUp(ownerName)}</Typography>
            </Stack>
          </Stack>
        </RenewalSummaryCard>
      </Container>
    </PortalLayout>
  );
}
